"use client";

import { cn, variantBackgroundColors } from "@/lib/utils";
import React from "react";
import { SpacingType, sectionVariants, ButtonStyleType } from "@/types";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { Badge } from "@/components/ui/badge";
import parse from "html-react-parser";
import Image from '@/components/ui/Image';
import { SectionWrapper } from "../components/SectionWraper";

interface FeatureItem {
  icon?: string;
  image?: string;
  imageAltText?: string;
  imageCaption?: string;
  title?: string;
  content?: string;
}

interface Feature10Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  items?: FeatureItem[];
  attributes?: React.HTMLProps<HTMLElement>;
}
const Feature10 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  usedInPageNav = false,
  pageNavTitle = "Feature 10",
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  items,
  attributes,
}: Feature10Props) => {
  const iconBackground = variantBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature10"
      attributes={attributes}
    > 
      <div className="container"> 
        <div className="mb-10 flex flex-col items-center gap-6 md:gap-12 md:mb-20" data-aos="fade-up">
          {badge && (
            <Badge variant="outline" bold>
              {badge}
            </Badge>
          )}
          {title && (
            <h2 className="text-center md:max-w-[500px] lg:max-w-[800px] md:mx-auto">
              {parse(title)}
            </h2>
          )}
          {content && (
            <div className="paragraph-lg text-muted-foreground text-center md:max-w-[65ch] mx-auto">
              {parse(content)}
            </div>
          )}
          {hasButton && (
            <div className="flex flex-col text-center gap-3">
              <Button asChild variant={buttonStyle} size="lg" className="w-full lg:w-auto">
                <Link href={buttonLink ?? ""}>
                  {buttonText}
                </Link>
              </Button>
              {microCopy && (
                <div className="paragraph-sm text-muted-foreground">
                  {microCopy}
                </div>
              )}
            </div>
          )}
        </div>  
        
        <div className="mx-auto grid gap-14 lg:gap-10 lg:grid-cols-3" data-aos="fade-up" data-aos-delay={100}>
          {items && items.length > 0 && items?.slice(0, 3).map((item, index) => (
            <div key={index} className="flex flex-col gap-6">
              <div className="relative">
                <Image
                  src={item.image ?? ""}
                  alt={item.imageAltText ?? ""}
                  title={item.imageAltText ?? ""}
                  width={840}
                  height={560}
                  className="aspect-[840/560] object-cover rounded-xl w-full"
                />
                {item.icon && (
                  <div className={cn(
                    "absolute top-4 left-4 w-12 h-12 rounded-full flex items-center justify-center",
                    iconBackground
                  )}>
                    <span className="material-symbols-outlined font-variation-filled text-brand">
                      {item.icon}
                    </span>
                  </div>
                )}
              </div>
              {item.imageCaption && (
                <p className="text-muted-foreground paragraph-base -mt-2">
                  {item.imageCaption}
                </p>
              )}
              {item.title && (
                <h5 className="lg:mt-6">
                  {item.title}
                </h5>
              )}
              {item.content && (  
                <div className="paragraph-base">
                  {parse(item.content ?? "")}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </SectionWrapper>
  );
};

export { Feature10 };